import React, { useState, useEffect } from 'react';
import axios from './utils/axiosConfig';
import { useAuth } from './context/AuthContext';
import { isSuperAdmin } from './utils/superAdmin';
import {
  Container,
  Table,
  Button,
  Modal,
  Form,
  Alert,
  Badge,
  ButtonGroup,
  Row,
  Col,
  Card
} from 'react-bootstrap';
import { MEMBER_TERM } from './config/clubTerms';

/**
 * 收入管理組件
 * 提供收入記錄的新增、查看、確認功能
 */
function IncomeManagement() {
  const { user } = useAuth();
  const token = localStorage.getItem('token');

  // 狀態管理
  const [incomes, setIncomes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Modal 狀態
  const [showModal, setShowModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [currentDetail, setCurrentDetail] = useState(null);

  // 新增收入表單
  const [newIncome, setNewIncome] = useState({
    ssid: user?.e_ssid || '',
    term_id: '',
    s_id: '',
    amount: 0,
    income_date: new Date().toISOString().split('T')[0],
    payer_name: '',
    payer_member_id: '',
    payment_method: 'transfer',
    receipt_number: '',
    memo: '',
    status: 'confirmed'
  });

  // 科目列表
  const [subjects, setSubjects] = useState([]);

  // 社別列表
  const [societies, setSocieties] = useState([]);

  // 屆別列表
  const [terms, setTerms] = useState([]);

  // 員工列表
  const [employees, setEmployees] = useState([]);

  // 篩選條件
  const [filterStatus, setFilterStatus] = useState('all');

  // 載入社別列表
  const loadSocieties = async () => {
    try {
      const response = await axios.get('/stores', {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data) {
        setSocieties(response.data);
      }
    } catch (err) {
      console.error('載入社別列表失敗:', err);
    }
  };

  // 載入屆別列表
  const loadTerms = async (ssid) => {
    try {
      if (!ssid) {
        setTerms([]);
        return;
      }

      const response = await axios.get('/finance/terms', {
        params: { ssid },
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        const termsData = response.data.data;
        setTerms(termsData);

        // 自動選擇啟用的屆別
        if (termsData.length > 0 && newIncome.ssid && !newIncome.term_id) {
          const activeTerm = termsData.find(term => term.is_active === 1 || term.is_active === '1');
          if (activeTerm) {
            setNewIncome(prev => ({ ...prev, term_id: activeTerm.term_id }));
          } else {
            setNewIncome(prev => ({ ...prev, term_id: termsData[0].term_id }));
          }
        }
      }
    } catch (err) {
      console.error('載入屆別列表失敗:', err);
    }
  };

  // 載入員工列表（社別下的社友 group=5）
  const loadEmployees = async (ssid) => {
    try {
      if (!ssid) {
        setEmployees([]);
        return;
      }

      const response = await axios.get('/employees', {
        params: {
          filter: '',
          limit: 500
        },
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data && response.data.data) {
        const filteredEmployees = response.data.data.filter(
          emp => emp.e_ssid === parseInt(ssid) && emp.e_group === 5
        );
        setEmployees(filteredEmployees);
      }
    } catch (err) {
      console.error('載入員工列表失敗:', err);
      setEmployees([]);
    }
  };

  // 載入科目列表（僅收入科目）
  const loadSubjects = async () => {
    try {
      const response = await axios.get('/finance/subjects', {
        params: { active_only: 'true' },
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        // 只顯示收入科目
        const incomeSubjects = response.data.data.filter(s => s.s_type === 'income');
        setSubjects(incomeSubjects);
      }
    } catch (err) {
      console.error('載入科目列表失敗:', err);
    }
  };

  // 載入收入記錄列表
  const loadIncomes = async () => {
    try {
      setLoading(true);
      setError(null);

      const params = {};
      if (filterStatus !== 'all') params.status = filterStatus;

      const response = await axios.get('/finance/income', {
        params,
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        setIncomes(response.data.data);
      }
    } catch (err) {
      setError('載入收入記錄列表失敗：' + (err.response?.data?.error || err.message));
    } finally {
      setLoading(false);
    }
  };

  // 初始化載入
  useEffect(() => {
    loadSocieties();
    loadSubjects();
    loadIncomes();

    if (newIncome.ssid) {
      loadTerms(newIncome.ssid);
      loadEmployees(newIncome.ssid);
    }
  }, [filterStatus]);

  // 當社別改變時載入對應的屆別和員工
  useEffect(() => {
    if (newIncome.ssid) {
      setNewIncome(prev => ({ ...prev, term_id: '' }));
      loadTerms(newIncome.ssid);
      loadEmployees(newIncome.ssid);
    } else {
      setTerms([]);
      setEmployees([]);
      setNewIncome(prev => ({ ...prev, term_id: '', payer_member_id: '' }));
    }
  }, [newIncome.ssid]);

  // 開啟新增收入彈窗
  const openNewIncomeModal = () => {
    setNewIncome({
      ssid: user?.e_ssid || '',
      term_id: '',
      s_id: '',
      amount: 0,
      income_date: new Date().toISOString().split('T')[0],
      payer_name: '',
      payer_member_id: '',
      payment_method: 'transfer',
      receipt_number: '',
      memo: '',
      status: 'confirmed'
    });

    if (user?.e_ssid) {
      loadTerms(user.e_ssid);
      loadEmployees(user.e_ssid);
    }

    setShowModal(true);
  };

  // 提交新增收入
  const handleSubmit = async () => {
    try {
      setError(null);
      setSuccess(null);

      // 驗證
      if (!newIncome.ssid || !newIncome.term_id || !newIncome.s_id) {
        setError('社別、屆別和科目為必填欄位');
        return;
      }

      if (!newIncome.amount || parseFloat(newIncome.amount) <= 0) {
        setError('請輸入正確的收入金額');
        return;
      }

      const response = await axios.post('/finance/income', {
        ...newIncome,
        created_by: user?.e_id
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        setSuccess('收入記錄新增成功！');
        setShowModal(false);
        setNewIncome({
          ssid: user?.e_ssid || '',
          term_id: '',
          s_id: '',
          amount: 0,
          income_date: new Date().toISOString().split('T')[0],
          payer_name: '',
          payer_member_id: '',
          payment_method: 'transfer',
          receipt_number: '',
          memo: '',
          status: 'confirmed'
        });
        loadIncomes();
      }
    } catch (err) {
      setError(err.response?.data?.error || '新增收入記錄失敗');
    }
  };

  // 查看收入詳情
  const viewDetail = async (income_id) => {
    try {
      const response = await axios.get(`/finance/income/${income_id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        setCurrentDetail(response.data.data);
        setShowDetailModal(true);
      }
    } catch (err) {
      setError('載入收入記錄詳情失敗：' + (err.response?.data?.error || err.message));
    }
  };

  // 刪除收入記錄
  const deleteIncome = async (income_id) => {
    try {
      if (!window.confirm('確定要刪除此收入記錄嗎？此操作無法復原！')) {
        return;
      }

      setError(null);
      setSuccess(null);

      const response = await axios.delete(`/finance/income/${income_id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        setSuccess('收入記錄刪除成功');
        setShowDetailModal(false);
        loadIncomes();
      }
    } catch (err) {
      setError(err.response?.data?.error || '刪除失敗');
    }
  };

  // 取得狀態顏色
  const getStatusColor = (status) => {
    return status === 'confirmed' ? 'success' : 'secondary';
  };

  // 取得狀態文字
  const getStatusText = (status) => {
    return status === 'confirmed' ? '已確認' : '草稿';
  };

  // 取得付款方式文字
  const getPaymentMethodText = (method) => {
    const texts = {
      cash: '現金',
      transfer: '轉帳',
      check: '支票',
      other: '其他'
    };
    return texts[method] || method;
  };

  // 格式化金額
  const formatAmount = (amount) => {
    return new Intl.NumberFormat('zh-TW', {
      style: 'currency',
      currency: 'TWD',
      minimumFractionDigits: 0
    }).format(amount || 0);
  };

  const isAdmin = isSuperAdmin(user?.e_id) || user?.e_position === 17;

  return (
    <Container fluid className="py-4">
      <h2 className="mb-4">💵 財務管理 - 收入管理</h2>

      {/* 錯誤/成功訊息 */}
      {error && <Alert variant="danger" onClose={() => setError(null)} dismissible>{error}</Alert>}
      {success && <Alert variant="success" onClose={() => setSuccess(null)} dismissible>{success}</Alert>}

      {/* 工具列 */}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <ButtonGroup>
          <Button
            variant={filterStatus === 'all' ? 'primary' : 'outline-primary'}
            onClick={() => setFilterStatus('all')}
          >
            全部
          </Button>
          <Button
            variant={filterStatus === 'confirmed' ? 'success' : 'outline-success'}
            onClick={() => setFilterStatus('confirmed')}
          >
            已確認
          </Button>
          <Button
            variant={filterStatus === 'draft' ? 'secondary' : 'outline-secondary'}
            onClick={() => setFilterStatus('draft')}
          >
            草稿
          </Button>
        </ButtonGroup>

        <Button variant="primary" onClick={openNewIncomeModal}>
          ➕ 新增收入記錄
        </Button>
      </div>

      {/* 收入記錄列表 */}
      {loading ? (
        <div className="text-center py-5">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">載入中...</span>
          </div>
        </div>
      ) : (
        <Table striped bordered hover responsive>
          <thead className="table-dark">
            <tr>
              <th style={{ width: '80px' }}>ID</th>
              <th style={{ width: '100px' }}>收入日期</th>
              <th>科目</th>
              <th>付款人/單位</th>
              <th style={{ width: '120px' }}>金額</th>
              <th style={{ width: '100px' }}>收款方式</th>
              <th style={{ width: '100px' }}>狀態</th>
              <th style={{ width: '150px' }}>操作</th>
            </tr>
          </thead>
          <tbody>
            {incomes.length === 0 ? (
              <tr>
                <td colSpan="8" className="text-center text-muted py-4">
                  目前沒有收入記錄
                </td>
              </tr>
            ) : (
              incomes.map(income => (
                <tr key={income.income_id}>
                  <td><code>{income.income_id}</code></td>
                  <td>{new Date(income.income_date).toLocaleDateString('zh-TW')}</td>
                  <td>{income.s_code} - {income.s_title}</td>
                  <td>{income.payer_member_name || income.payer_name || '-'}</td>
                  <td className="text-end">{formatAmount(income.amount)}</td>
                  <td>{getPaymentMethodText(income.payment_method)}</td>
                  <td>
                    <Badge bg={getStatusColor(income.status)}>
                      {getStatusText(income.status)}
                    </Badge>
                  </td>
                  <td>
                    <ButtonGroup size="sm">
                      <Button
                        variant="outline-primary"
                        onClick={() => viewDetail(income.income_id)}
                      >
                        查看
                      </Button>
                      {income.status === 'draft' && (
                        <Button
                          variant="outline-danger"
                          onClick={() => deleteIncome(income.income_id)}
                        >
                          🗑️
                        </Button>
                      )}
                    </ButtonGroup>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </Table>
      )}

      {/* 新增收入 Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>新增收入記錄</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Row>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>社別 <span className="text-danger">*</span></Form.Label>
                  {isAdmin ? (
                    <Form.Select
                      value={newIncome.ssid}
                      onChange={(e) => setNewIncome({ ...newIncome, ssid: e.target.value })}
                    >
                      <option value="">請選擇社別</option>
                      {societies.map(society => (
                        <option key={society.ss_id} value={society.ss_id}>
                          {society.ss_name}
                        </option>
                      ))}
                    </Form.Select>
                  ) : (
                    <Form.Control
                      type="text"
                      value={societies.find(s => s.ss_id === newIncome.ssid)?.ss_name || ''}
                      readOnly
                      disabled
                    />
                  )}
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>屆別 <span className="text-danger">*</span></Form.Label>
                  <Form.Select
                    value={newIncome.term_id}
                    onChange={(e) => setNewIncome({ ...newIncome, term_id: e.target.value })}
                    disabled={!newIncome.ssid}
                  >
                    <option value="">請選擇屆別</option>
                    {terms.map(term => (
                      <option key={term.term_id} value={term.term_id}>
                        {term.term_name}（第 {term.term_no} 屆）
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>收入科目 <span className="text-danger">*</span></Form.Label>
                  <Form.Select
                    value={newIncome.s_id}
                    onChange={(e) => setNewIncome({ ...newIncome, s_id: e.target.value })}
                  >
                    <option value="">請選擇科目</option>
                    {subjects.map(s => (
                      <option key={s.s_id} value={s.s_id}>
                        {s.s_code} - {s.s_title}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>收入金額 <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    type="number"
                    step="0.01"
                    value={newIncome.amount}
                    onChange={(e) => setNewIncome({ ...newIncome, amount: e.target.value })}
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>收入日期 <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    type="date"
                    value={newIncome.income_date}
                    onChange={(e) => setNewIncome({ ...newIncome, income_date: e.target.value })}
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>收款方式</Form.Label>
                  <Form.Select
                    value={newIncome.payment_method}
                    onChange={(e) => setNewIncome({ ...newIncome, payment_method: e.target.value })}
                  >
                    <option value="transfer">轉帳</option>
                    <option value="cash">現金</option>
                    <option value="check">支票</option>
                    <option value="other">其他</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>付款人/單位</Form.Label>
                  <Form.Control
                    type="text"
                    value={newIncome.payer_name}
                    onChange={(e) => setNewIncome({ ...newIncome, payer_name: e.target.value })}
                    placeholder="請輸入付款人或單位名稱"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>付款{MEMBER_TERM}（選填）</Form.Label>
                  <Form.Select
                    value={newIncome.payer_member_id}
                    onChange={(e) => setNewIncome({ ...newIncome, payer_member_id: e.target.value })}
                    disabled={!newIncome.ssid}
                  >
                    <option value="">非{MEMBER_TERM}或不指定</option>
                    {employees.map(emp => (
                      <option key={emp.e_id} value={emp.e_id}>
                        {emp.e_name}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Label>收據/憑證編號</Form.Label>
              <Form.Control
                type="text"
                value={newIncome.receipt_number}
                onChange={(e) => setNewIncome({ ...newIncome, receipt_number: e.target.value })}
                placeholder="請輸入收據或憑證編號（選填）"
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>備註</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={newIncome.memo}
                onChange={(e) => setNewIncome({ ...newIncome, memo: e.target.value })}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>狀態</Form.Label>
              <Form.Select
                value={newIncome.status}
                onChange={(e) => setNewIncome({ ...newIncome, status: e.target.value })}
              >
                <option value="confirmed">已確認（立即入帳）</option>
                <option value="draft">草稿（暫不入帳）</option>
              </Form.Select>
              <Form.Text className="text-muted">
                選擇「已確認」將自動產生交易記錄並更新預算進度
              </Form.Text>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            取消
          </Button>
          <Button variant="primary" onClick={handleSubmit}>
            新增
          </Button>
        </Modal.Footer>
      </Modal>

      {/* 收入詳情 Modal */}
      {currentDetail && (
        <Modal show={showDetailModal} onHide={() => setShowDetailModal(false)} size="lg">
          <Modal.Header closeButton>
            <Modal.Title>收入記錄詳情 #{currentDetail.income_id}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Row className="mb-3">
              <Col md={6}>
                <strong>收入日期:</strong> {new Date(currentDetail.income_date).toLocaleDateString('zh-TW')}<br />
                <strong>科目:</strong> {currentDetail.s_code} - {currentDetail.s_title}<br />
                <strong>金額:</strong> {formatAmount(currentDetail.amount)}<br />
                <strong>狀態:</strong> <Badge bg={getStatusColor(currentDetail.status)}>{getStatusText(currentDetail.status)}</Badge>
              </Col>
              <Col md={6}>
                <strong>付款人/單位:</strong> {currentDetail.payer_member_name || currentDetail.payer_name || '-'}<br />
                <strong>收款方式:</strong> {getPaymentMethodText(currentDetail.payment_method)}<br />
                <strong>收據編號:</strong> {currentDetail.receipt_number || '-'}<br />
                <strong>建立者:</strong> {currentDetail.creator_name}
              </Col>
            </Row>

            {currentDetail.memo && (
              <Alert variant="info">
                <strong>備註:</strong> {currentDetail.memo}
              </Alert>
            )}

            {currentDetail.transactions && currentDetail.transactions.length > 0 && (
              <>
                <h5 className="mt-3">交易記錄</h5>
                <Table striped bordered size="sm">
                  <thead>
                    <tr>
                      <th>交易ID</th>
                      <th>日期</th>
                      <th>金額</th>
                      <th>備註</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentDetail.transactions.map((txn, index) => (
                      <tr key={index}>
                        <td>{txn.txn_id}</td>
                        <td>{new Date(txn.txn_date).toLocaleDateString('zh-TW')}</td>
                        <td className="text-end">{formatAmount(txn.amount)}</td>
                        <td>{txn.memo}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </>
            )}
          </Modal.Body>
          <Modal.Footer>
            {currentDetail.status === 'draft' && (
              <Button
                variant="outline-danger"
                onClick={() => deleteIncome(currentDetail.income_id)}
              >
                🗑️ 刪除
              </Button>
            )}
            <Button variant="secondary" onClick={() => setShowDetailModal(false)}>
              關閉
            </Button>
          </Modal.Footer>
        </Modal>
      )}
    </Container>
  );
}

export default IncomeManagement;
